import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { driveAPI } from '../../services/api'
import RecruitmentRoundEditor from '../../components/RecruitmentRoundEditor'
import { RoundConfig } from '../../services/recruitmentAPI'
import CollegeSelect from '../../components/CollegeSelect'
import { College } from '../../services/collegeAPI'
import { AssessmentConfig, DriveAssessmentConfigs } from '../../types'

interface DriveForm {
  title: string
  description: string
  startDate: string
  endDate: string
  maxCandidates: number
  instructions: string
  negativeMarking: number
  collegeType: 'engineering' | 'management' | ''
  tier: 'tier1' | 'tier2' | ''
  role: 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | 'AI/ML-Assignment' | 'AIQ Test' | ''
}

interface AssessmentTypeConfig {
  name: string
  type: string
  enabled: boolean
  duration: number
  numQuestions: number
}

const DURATION_OPTIONS = [30, 45, 60, 75, 90]

const CreateDrivePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [roundConfig, setRoundConfig] = useState<RoundConfig>()
  
  // Set default start date to tomorrow
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowString = tomorrow.toISOString().slice(0, 16)

  // Set default end date to day after tomorrow
  const dayAfterTomorrow = new Date()
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2)
  const dayAfterTomorrowString = dayAfterTomorrow.toISOString().slice(0, 16)

  const [formData, setFormData] = useState<DriveForm>({
    title: '',
    description: '',
    startDate: tomorrowString,
    endDate: dayAfterTomorrowString,
    maxCandidates: 100,
    instructions: '',
    negativeMarking: 0,
    collegeType: location.state?.preSelectedCollegeType || '',
    tier: location.state?.preSelectedTier || '',
    role: ''
  })

  // Assessment configurations
  const [assessmentConfigs, setAssessmentConfigs] = useState<AssessmentTypeConfig[]>([])

  useEffect(() => {
    // Update form data when navigation state changes
    if (location.state?.preSelectedCollegeType || location.state?.preSelectedTier) {
      setFormData(prev => ({
        ...prev,
        collegeType: location.state?.preSelectedCollegeType || '',
        tier: location.state?.preSelectedTier || '',
        role: ''
      }))
    }
  }, [location.state])

  const [selectedCollege, setSelectedCollege] = useState<College | null>(null)
  useEffect(() => { setSelectedCollege(null) }, [formData.collegeType, formData.tier])

  // Initialize assessment configs based on college type and role
  useEffect(() => {
    if (!formData.collegeType || !formData.tier) {
      setAssessmentConfigs([])
      return
    }

    if (formData.collegeType === 'engineering') {
      const baseAssessments: AssessmentTypeConfig[] = [
        { name: 'Aptitude & Reasoning', type: 'aptitude_reasoning', enabled: true, duration: 45, numQuestions: 20 }
      ]

      // Configure assessments based on role
      if (formData.role === 'ASE') {
        baseAssessments.push(
          { name: 'Computer Fundamentals', type: 'fundamentals', enabled: true, duration: 30, numQuestions: 10 },
          { name: 'Coding Assessment', type: 'coding', enabled: true, duration: 60, numQuestions: 3 }
        )
      } else if (formData.role === 'Cloud Support Engineer') {
        baseAssessments.push(
          { name: 'Cloud Fundamentals', type: 'cloud_fundamentals', enabled: true, duration: 30, numQuestions: 10 },
          { name: 'English Communication', type: 'english_communication', enabled: true, duration: 30, numQuestions: 10 }
        )
      } else if (formData.role === 'Pre-Sales') {
        baseAssessments.push(
          { name: 'Pre-Sales Fundamentals', type: 'presales_fundamentals', enabled: true, duration: 30, numQuestions: 10 },
          { name: 'English Communication', type: 'english_communication', enabled: true, duration: 30, numQuestions: 10 }
        )
      } else if (formData.role === 'AIQ Test') {
        baseAssessments.push(
          { name: 'AI Fundamental', type: 'ai_fundamental', enabled: true, duration: 30, numQuestions: 10 }
        )
      } else if (formData.role === 'AI/ML-Assignment') {
        // Only AI/ML Assignment, no Aptitude
        setAssessmentConfigs([
          { name: 'AI/ML Assignment', type: 'aiml_assignment', enabled: true, duration: 60, numQuestions: 1 }
        ])
        return
      }

      setAssessmentConfigs(baseAssessments)
    } else if (formData.collegeType === 'management') {
      setAssessmentConfigs([
        { name: 'Quantitative Ability', type: 'quantitative_ability', enabled: true, duration: 30, numQuestions: 20 },
        { name: 'Logical Reasoning', type: 'logical_reasoning', enabled: true, duration: 30, numQuestions: 20 },
        { name: 'Verbal Ability', type: 'verbal_ability', enabled: true, duration: 30, numQuestions: 20 }
      ])
    }
  }, [formData.collegeType, formData.tier, formData.role])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxCandidates' ? parseInt(value) || 0 : name === 'negativeMarking' ? parseFloat(value) || 0 : value,
      // Reset tier and role when college type changes
      ...(name === 'collegeType' && { tier: '', role: '' }),
      // Reset role when tier changes
      ...(name === 'tier' && { role: '' })
    }))
  }

  const handleAssessmentConfigChange = (index: number, field: 'duration' | 'numQuestions' | 'enabled', value: number | boolean) => {
    setAssessmentConfigs(prev => {
      const updated = [...prev]
      if (field === 'enabled') {
        updated[index].enabled = value as boolean
      } else if (field === 'duration') {
        updated[index].duration = value as number
      } else if (field === 'numQuestions') {
        updated[index].numQuestions = value as number
      }
      return updated
    })
  }

  const getTotalDuration = () => {
    return assessmentConfigs
      .filter(config => config.enabled)
      .reduce((sum, config) => sum + config.duration, 0)
  }

  const getTotalQuestions = () => {
    return assessmentConfigs
      .filter(config => config.enabled)
      .reduce((sum, config) => sum + config.numQuestions, 0)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate dates
      const now = new Date()
      const startDate = new Date(formData.startDate)
      const endDate = new Date(formData.endDate)

      // Check if start date is in the past
      if (startDate <= now) {
        toast.error('Start date must be in the future')
        setIsSubmitting(false)
        return
      }

      // Check if end date is before start date
      if (endDate <= startDate) {
        toast.error('End date must be after start date')
        setIsSubmitting(false)
        return
      }

      // Validate college type and tier
      if (!formData.collegeType) {
        toast.error('Please select a college type')
        setIsSubmitting(false)
        return
      }

      if (!formData.tier) {
        toast.error('Please select a tier')
        setIsSubmitting(false)
        return
      }

      if (!selectedCollege) { toast.error('Please select a college'); setIsSubmitting(false); return }

      // Validate role for engineering colleges
      if (formData.collegeType === 'engineering' && !formData.role) {
        toast.error('Please select a role')
        setIsSubmitting(false)
        return
      }

      // Validate at least one assessment is enabled
      const enabledAssessments = assessmentConfigs.filter(config => config.enabled)
      if (enabledAssessments.length === 0) {
        toast.error('Please enable at least one assessment type')
        setIsSubmitting(false)
        return
      }

      // Build assessment_configs object
      const assessment_configs: DriveAssessmentConfigs = {}
      enabledAssessments.forEach(config => {
        assessment_configs[config.type] = {
          duration_minutes: config.duration,
          num_questions: config.numQuestions
        }
      })

      // IMPORTANT: Format dates WITHOUT timezone conversion to preserve exact time entered
      // datetime-local format: "2025-11-01T15:42" -> send as "2025-11-01T15:42:00" (naive, no timezone)
      const formatDateTimeForAPI = (dateTimeLocal: string): string => {
        // datetime-local gives format: "YYYY-MM-DDTHH:mm"
        // Convert to: "YYYY-MM-DDTHH:mm:00" (add seconds, NO timezone info)
        if (dateTimeLocal && dateTimeLocal.includes('T')) {
          const parts = dateTimeLocal.split('T')
          const datePart = parts[0]
          const timePart = parts[1] || '00:00'
          // Ensure time has seconds
          const timeWithSeconds = timePart.split(':').length === 2 
            ? `${timePart}:00` 
            : timePart
          const formatted = `${datePart}T${timeWithSeconds}`
          console.log(`Frontend: Converting "${dateTimeLocal}" -> "${formatted}" (no timezone, preserving local time)`)
          return formatted
        }
        return dateTimeLocal
      }

      // Prepare drive data for API
      const driveData = {
        title: formData.title,
        description: formData.description,
        recruitment_round_ids: roundConfig?.round_ids,
        assessment_cutoff: roundConfig?.cutoff ?? 70,
        college_id: selectedCollege.college_id,
        college_type: formData.collegeType,
        tier: formData.tier,
        role: formData.collegeType === 'engineering' ? formData.role : undefined,
        start_date: formatDateTimeForAPI(formData.startDate),
        end_date: formatDateTimeForAPI(formData.endDate),
        max_candidates: formData.maxCandidates,
        instructions: formData.instructions,
        assessment_configs: assessment_configs,
        drive_metadata: {
          negative_marking: formData.negativeMarking || 0
        }
      }

      // Create drive via API
      const response = await driveAPI.createDrive(driveData)
      const createdDrive = response.data

      toast.success('Drive created successfully!')

      // Show registration link
      const registrationUrl = `${window.location.origin}/register/${createdDrive.registration_link}`
      toast.success(
        <div className="bg-white border border-green-200 rounded-md p-3 shadow-md w-80">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">Drive Created!</div>
              <div className="text-xs text-gray-600 truncate">{registrationUrl}</div>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(registrationUrl)
                toast.success('Copied!')
              }}
              className="flex-shrink-0 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            >
              Copy
            </button>
          </div>
        </div>,
        {
          duration: 4000,
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0
          }
        }
      )

      navigate('/dashboard')
    } catch (error: any) {
      console.error('Error creating drive:', error)
      const errorMessage = error.response?.data?.detail || 'Failed to create drive. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/dashboard')
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <PlusIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Drive</h1>
              <p className="text-gray-600 mt-1">Set up a new recruitment drive with custom assessment configurations</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Drive Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Software Engineer Drive 2024"
                  />
                </div>

                <div>
                  <label htmlFor="maxCandidates" className="block text-sm font-medium text-gray-700 mb-2">
                    Max Candidates *
                  </label>
                  <input
                    type="number"
                    id="maxCandidates"
                    name="maxCandidates"
                    required
                    min="1"
                    value={formData.maxCandidates}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* College Type and Tier Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="collegeType" className="block text-sm font-medium text-gray-700 mb-2">
                    College Type *
                  </label>
                  <select
                    id="collegeType"
                    name="collegeType"
                    required
                    value={formData.collegeType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select College Type</option>
                    <option value="engineering">Engineering Colleges</option>
                    <option value="management">Management Colleges</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tier" className="block text-sm font-medium text-gray-700 mb-2">
                    Tier *
                  </label>
                  <select
                    id="tier"
                    name="tier"
                    required
                    value={formData.tier}
                    onChange={handleInputChange}
                    disabled={!formData.collegeType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Tier</option>
                    {formData.collegeType === 'engineering' && (
                      <>
                        <option value="tier1">Tier 1 (IITs, NITs and IIITs)</option>
                        <option value="tier2">Tier 2 (Other Engineering Colleges)</option>
                      </>
                    )}
                    {formData.collegeType === 'management' && (
                      <>
                        <option value="tier1">Tier 1 (IIMs & Top MBA Institutes)</option>
                        <option value="tier2">Tier 2 (Other MBA Colleges)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <CollegeSelect id="driveCollege" value={selectedCollege} onChange={setSelectedCollege}
                  collegeType={formData.collegeType} tier={formData.tier} disabled={!formData.collegeType || !formData.tier} />
              </div>

              {/* Role Selection - Only for Engineering Colleges */}
              {formData.collegeType === 'engineering' && formData.tier && (
                <div className="mt-6">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Role</option>
                    <option value="ASE">ASE (Associate Software Engineer)</option>
                    <option value="Cloud Support Engineer">Cloud Support Engineer</option>
                    <option value="Pre-Sales">Pre-Sales</option>
                    <option value="AIQ Test">AIQ Test</option>
                    <option value="AI/ML-Assignment">AI/ML-Assignment</option>
                  </select>
                </div>
              )}

              <div className="mt-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the drive, requirements, and what candidates can expect..."
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    name="startDate"
                    required
                    min={new Date().toISOString().slice(0, 16)}
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    name="endDate"
                    required
                    min={formData.startDate || new Date().toISOString().slice(0, 16)}
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Assessment Configuration */}
            {assessmentConfigs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Configuration</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure duration and number of questions for each assessment type
                </p>

                <div className="space-y-4">
                  {assessmentConfigs.map((config, index) => (
                    <div
                      key={config.type}
                      className={`border rounded-lg p-4 ${
                        config.enabled ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex items-center h-10">
                          <input
                            type="checkbox"
                            id={`enable-${config.type}`}
                            checked={config.enabled}
                            onChange={(e) => handleAssessmentConfigChange(index, 'enabled', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>

                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              {config.name}
                            </label>
                            <p className="text-xs text-gray-500">Assessment Type</p>
                          </div>

                          <div>
                            <label htmlFor={`duration-${config.type}`} className="block text-sm font-medium text-gray-700 mb-2">
                              Duration (minutes) *
                            </label>
                            <select
                              id={`duration-${config.type}`}
                              value={config.duration}
                              onChange={(e) => handleAssessmentConfigChange(index, 'duration', parseInt(e.target.value))}
                              disabled={!config.enabled}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              {DURATION_OPTIONS.map(duration => (
                                <option key={duration} value={duration}>{duration} min</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor={`questions-${config.type}`} className="block text-sm font-medium text-gray-700 mb-2">
                              Number of Questions *
                            </label>
                            <input
                              type="number"
                              id={`questions-${config.type}`}
                              value={config.numQuestions}
                              onChange={(e) => handleAssessmentConfigChange(index, 'numQuestions', parseInt(e.target.value) || 1)}
                              min="1"
                              max="50"
                              disabled={!config.enabled}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              style={{
                                MozAppearance: 'textfield',
                                WebkitAppearance: 'none'
                              } as React.CSSProperties}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-green-900">Total Test Summary</h4>
                      <p className="text-xs text-green-700 mt-1">
                        Based on your configuration
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-900">{getTotalDuration()} min</div>
                      <div className="text-sm text-green-700">{getTotalQuestions()} questions</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
              <div>
                <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-2">
                  Test Instructions
                </label>
                <textarea
                  id="instructions"
                  name="instructions"
                  rows={4}
                  value={formData.instructions}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide specific instructions for candidates taking the test..."
                />
              </div>
            </div>

            {/* Negative Marking */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scoring Configuration</h3>
              <div>
                <label htmlFor="negativeMarking" className="block text-sm font-medium text-gray-700 mb-2">
                  Negative Marking (per incorrect answer)
                </label>
                <select
                  id="negativeMarking"
                  name="negativeMarking"
                  value={formData.negativeMarking}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0}>0 (No negative marking)</option>
                  <option value={0.25}>0.25</option>
                  <option value={0.30}>0.30</option>
                  <option value={0.50}>0.50</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select the negative marking value. For example, 0.25 means each incorrect answer deducts 0.25 marks.
                  Final Score = w1*((m1-q*(n1-m1))/n1) + w2*((m2-q*(n2-m2))/n2) + w3*((m3-q*(n3-m3))/n3)
                </p>
              </div>
            </div>

            <section className="border-t pt-6 space-y-4"><h3 className="text-lg font-semibold">Recruitment Rounds</h3><RecruitmentRoundEditor value={roundConfig} onChange={setRoundConfig} disabled={isSubmitting} /></section>
            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || assessmentConfigs.filter(c => c.enabled).length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Create Drive
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateDrivePage
